/**
 * ExportActivityChart
 *
 * Stacked bar chart showing exported vs unexported hours per attorney.
 * Sorted by export percentage descending (highest exporters on the left).
 * Each bar is one attorney with Exported (green) stacked below Not Exported (red).
 * The timeframe slider lives in the card header.
 */

import { useMemo } from "react";
import {
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { AttorneyExportStats } from "../../types/dashboard";
import { CHART_COLORS, CHART_CONFIG, TEXT_COLORS } from "../../config/dashboardTheme";
import TimeframeDropdown from "./TimeframeDropdown";

interface ExportActivityChartProps {
  attorneys: AttorneyExportStats[];
  loading?: boolean;
  timeframe: string;
  onTimeframeChange: (value: string) => void;
}

interface ChartRow {
  name: string;
  fullName: string;
  exported: number;
  unexported: number;
  total: number;
  exportPct: number;
}

export default function ExportActivityChart({
  attorneys,
  loading = false,
  timeframe,
  onTimeframeChange,
}: ExportActivityChartProps) {
  const chartData = useMemo<ChartRow[]>(() => {
    return [...attorneys]
      .sort((a, b) => b.exported_pct - a.exported_pct)
      .map((a) => ({
        name: a.name.length > 12 ? a.name.slice(0, 11) + "\u2026" : a.name,
        fullName: a.name,
        exported: a.exported_hours,
        unexported: a.unexported_hours,
        total: a.total_hours,
        exportPct: a.exported_pct,
      }));
  }, [attorneys]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ChartRow }>;
  }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
          <p className="font-semibold mb-2">{data.fullName}</p>
          <div className="space-y-1">
            <p className={TEXT_COLORS.exported.light}>
              Exported: {data.exported.toFixed(1)} hrs
            </p>
            <p className={TEXT_COLORS.unexported.light}>
              Not Exported: {data.unexported.toFixed(1)} hrs
            </p>
            <p className="text-muted-foreground">
              Total: {data.total.toFixed(1)} hrs
            </p>
            <p className="font-medium">Export Rate: {data.exportPct}%</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-xs p-6">
      {/* Card header: title left, timeframe slider right */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold">Export Activity by Attorney</h3>
        <TimeframeDropdown value={timeframe} onChange={onTimeframeChange} />
      </div>

      {loading ? (
        <div className="py-8 text-center text-muted-foreground">
          Loading export data...
        </div>
      ) : attorneys.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          No export data available for the selected timeframe.
        </div>
      ) : (
        <div className="w-full h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={CHART_COLORS.grid.line}
                className="opacity-50"
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: CHART_COLORS.grid.axis }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
                axisLine={{ stroke: CHART_COLORS.grid.axis, strokeWidth: 1 }}
                tickLine={false}
              />
              <YAxis
                label={{
                  value: "Hours",
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    textAnchor: "middle",
                    fill: CHART_COLORS.grid.axis,
                    fontSize: 12,
                  },
                }}
                tick={{ fontSize: 12, fill: CHART_COLORS.grid.axis }}
                axisLine={{ stroke: CHART_COLORS.grid.axis, strokeWidth: 1 }}
                tickLine={{ stroke: CHART_COLORS.grid.axis }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                content={({ payload }) => (
                  <div className="flex justify-center gap-6 text-sm">
                    {payload?.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-xs"
                          style={{
                            backgroundColor:
                              entry.value === "Exported"
                                ? CHART_COLORS.exported.stroke
                                : CHART_COLORS.unexported.stroke,
                          }}
                        />
                        <span className="text-muted-foreground">
                          {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              />
              <Bar
                dataKey="exported"
                name="Exported"
                stackId="stack"
                fill={CHART_COLORS.exported.fill}
                maxBarSize={CHART_CONFIG.maxBarSize}
                radius={CHART_CONFIG.bottomBarRadius}
              />
              <Bar
                dataKey="unexported"
                name="Not Exported"
                stackId="stack"
                fill={CHART_COLORS.unexported.fill}
                maxBarSize={CHART_CONFIG.maxBarSize}
                radius={CHART_CONFIG.topBarRadius}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
